import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { ApiError, type BillingOverview, type Payment, type PaymentMethod, type Plan, type PlanPriceSummary } from '../api';
import { useSession } from '../App';
import { navigate } from '../router';
import { formatDate, formatMoney, periodLabel } from '../format';
import { Spinner } from './ui';

export interface WallChoice {
  plan: Plan;
  price: PlanPriceSummary;
  kind: 'extend' | 'upgrade' | 'buy';
}

type Step = 'pay' | 'redirect' | 'wait' | 'done' | 'failed';

const BLIK_SECONDS = 120;
const TERMS_URL = 'https://static.payu.com/sites/terms/files/payu_terms_of_service_single_transaction_pl_en.pdf';
const PRIVACY_URL = 'https://static.payu.com/sites/terms/files/payu_privacy_policy_en_en.pdf';

/**
 * Pay for a plan without leaving the page where possible: a BLIK code typed
 * here and confirmed in the bank's app, or a card entered into PayU's own
 * Secure Form fields (iframes served by PayU — the card number never touches
 * this page's code or our servers), followed by the bank's 3-D Secure check.
 * Anything else goes to PayU's hosted page. Whatever happens here, what was
 * paid is decided by the API asking PayU.
 */
export function PaymentWall({
  choice,
  billing,
  onClose,
  onPaid,
}: {
  choice: WallChoice | null;
  billing: BillingOverview;
  onClose: () => void;
  onPaid: () => void;
}) {
  const { client, claims } = useSession();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const methods = billing.checkout.methods.length ? billing.checkout.methods : (['hosted'] as PaymentMethod[]);
  const [method, setMethod] = useState<PaymentMethod>(methods[0]!);
  const [step, setStep] = useState<Step>('pay');
  const [email, setEmail] = useState('');
  const [blikCode, setBlikCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [fieldError, setFieldError] = useState<{ field: string; message: string } | null>(null);
  const [inProgressId, setInProgressId] = useState<string | null>(null);
  const [payment, setPayment] = useState<Payment | null>(null);
  const [deadline, setDeadline] = useState(0);
  const [now, setNow] = useState(Date.now());
  const card = useSecureForm(billing, method === 'card' && choice !== null);
  const pollRef = useRef<ReturnType<typeof setTimeout>>();

  const open = choice !== null;
  // The last choice stays rendered after closing, so PayU's card iframes are
  // never unmounted: they are mounted once and only cleared afterwards.
  const [current, setCurrent] = useState<WallChoice | null>(null);
  useEffect(() => {
    if (choice) setCurrent(choice);
  }, [choice]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      setStep('pay');
      setMethod(methods[0]!);
      setBlikCode('');
      setFieldError(null);
      setInProgressId(null);
      setPayment(null);
      setEmail((current) => current || (typeof claims?.email === 'string' ? claims.email : ''));
      dialog.showModal();
    }
    if (!open && dialog.open) dialog.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => () => clearTimeout(pollRef.current), []);

  useEffect(() => {
    if (step !== 'wait') return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [step]);

  const shown = choice ?? current;
  if (!shown) return <dialog ref={dialogRef} className="wall" />;
  const { plan, price } = shown;
  const amount = formatMoney(price.amountMinor, price.currency);
  const shortName = plan.name.replace(/^DeskTouch\s+/i, '');
  const needsEmail = method !== 'hosted';

  function close() {
    clearTimeout(pollRef.current);
    if (step === 'done') onPaid();
    onClose();
  }

  function waitForBank(paymentId: string) {
    setStep('wait');
    const until = Date.now() + BLIK_SECONDS * 1000;
    setDeadline(until);
    setNow(Date.now());
    const tick = async () => {
      try {
        const next = await client.payment(paymentId);
        setPayment(next);
        if (next.status === 'completed') return setStep('done');
        if (next.status !== 'pending') return setStep('failed');
      } catch {
        /* a network hiccup: ask again */
      }
      // PayU may still be settling just after the code's life.
      if (Date.now() > until + 30_000) return setStep('failed');
      pollRef.current = setTimeout(() => void tick(), 2000);
    };
    pollRef.current = setTimeout(() => void tick(), 1500);
  }

  async function start(extra: { blikCode?: string; cardToken?: string }) {
    setFieldError(null);
    setInProgressId(null);
    try {
      const result = await client.checkout({
        priceKey: price.key,
        method,
        ...(needsEmail ? { email: email.trim() } : {}),
        ...extra,
      });
      if (result.action === 'redirect') {
        setStep('redirect');
        window.location.assign(result.redirectUrl);
        return;
      }
      waitForBank(result.paymentId);
    } catch (error) {
      setBusy(false);
      if (error instanceof ApiError) {
        const details = (error.details ?? {}) as { field?: string; paymentId?: string };
        if (error.code === 'payment_in_progress' && details.paymentId) setInProgressId(details.paymentId);
        const field = details.field === 'email' ? 'email' : details.field === 'blikCode' ? 'blik' : details.field === 'card' ? 'card' : 'form';
        setFieldError({ field, message: error.message });
        return;
      }
      setFieldError({ field: 'form', message: 'No connection to DeskTouch. Check your internet and try again.' });
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    if (needsEmail && !/^\S+@\S+\.\S+$/.test(email.trim())) {
      setFieldError({ field: 'email', message: 'Enter the email address for your receipt.' });
      return;
    }
    setBusy(true);
    if (method === 'blik') {
      const code = blikCode.replace(/\D/g, '');
      if (code.length !== 6) {
        setBusy(false);
        setFieldError({ field: 'blik', message: 'A BLIK code has 6 digits.' });
        return;
      }
      await start({ blikCode: code });
    } else if (method === 'card') {
      const token = await card.tokenize();
      if ('error' in token) {
        setBusy(false);
        setFieldError({ field: 'card', message: token.error });
        return;
      }
      await start({ cardToken: token.token });
    } else {
      await start({});
    }
    setBusy(false);
  }

  const secondsLeft = Math.max(0, Math.round((deadline - now) / 1000));

  return (
    <dialog
      ref={dialogRef}
      className="wall"
      aria-labelledby="wall-title"
      onCancel={(event) => {
        event.preventDefault();
        if (step !== 'redirect' && !busy) close();
      }}
    >
      <header className="wall-head">
        <div>
          <p className="eyebrow">{shown.kind === 'extend' ? 'Extend' : shown.kind === 'upgrade' ? 'Upgrade' : 'Subscribe'}</p>
          <h2 id="wall-title">
            {shortName} · 1 {periodLabel(price.intervalMonths)}
          </h2>
        </div>
        <p className="wall-amount">{amount}</p>
      </header>

      <form className="wall-body" onSubmit={submit} noValidate hidden={step !== 'pay'}>
          <p className="muted small-print">
            {shown.kind === 'extend'
              ? `Adds 1 ${periodLabel(price.intervalMonths)} after your current period (${formatDate(billing.subscription.currentPeriodEnd)}).`
              : shown.kind === 'upgrade' && billing.subscription.renewal === 'manual'
                ? `${shortName} starts once paid. Unused time on your current plan is converted into ${shortName} time at its value.`
                : `${shortName} starts as soon as the payment goes through. It does not renew by itself.`}
          </p>

          <div className="method-tabs" role="radiogroup" aria-label="Payment method">
            {methods.map((option) => (
              <button
                type="button"
                role="radio"
                aria-checked={method === option}
                key={option}
                className={method === option ? 'on' : ''}
                onClick={() => {
                  setMethod(option);
                  setFieldError(null);
                }}
              >
                <MethodIcon method={option} />
                <span>{option === 'blik' ? 'BLIK' : option === 'card' ? 'Card' : 'Other methods'}</span>
              </button>
            ))}
          </div>

          {method === 'blik' && (
            <label className="field">
              <span>BLIK code</span>
              <input
                className="blik-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000 000"
                value={blikCode}
                aria-invalid={fieldError?.field === 'blik'}
                autoFocus
                onChange={(event) => {
                  const digits = event.target.value.replace(/\D/g, '').slice(0, 6);
                  setBlikCode(digits.length > 3 ? `${digits.slice(0, 3)} ${digits.slice(3)}` : digits);
                  setFieldError(null);
                }}
              />
              <small>Generate the 6-digit code in your banking app, then confirm the payment there.</small>
              {fieldError?.field === 'blik' && <small className="error-text">{fieldError.message}</small>}
            </label>
          )}

          <div className="card-fields" hidden={method !== 'card'}>
            {card.state === 'loading' && <Spinner label="Loading the secure card form…" />}
            {card.state === 'failed' && (
              <p className="error-text">The card form could not load. Choose “Other methods” to pay by card on PayU's page.</p>
            )}
            <div className="card-grid" hidden={card.state === 'failed'}>
              <label className="field span-2">
                <span>Card number</span>
                <div id="payu-card-number" className="payu-field" />
              </label>
              <label className="field">
                <span>Expiry date</span>
                <div id="payu-card-date" className="payu-field" />
              </label>
              <label className="field">
                <span>Security code</span>
                <div id="payu-card-cvv" className="payu-field" />
              </label>
            </div>
            <small className="muted">Card details go straight to PayU; DeskTouch never sees them. Your bank may ask you to confirm (3-D Secure).</small>
            {fieldError?.field === 'card' && <small className="error-text">{fieldError.message}</small>}
          </div>

          {method === 'hosted' && (
            <p className="muted">
              Pay on PayU's page by bank transfer, card, Google Pay, Apple Pay or pay-later, then come straight back here.
            </p>
          )}

          {needsEmail && (
            <label className="field">
              <span>Email for the receipt</span>
              <input
                type="email"
                autoComplete="email"
                value={email}
                aria-invalid={fieldError?.field === 'email'}
                onChange={(event) => {
                  setEmail(event.target.value);
                  if (fieldError?.field === 'email') setFieldError(null);
                }}
              />
              {fieldError?.field === 'email' && <small className="error-text">{fieldError.message}</small>}
            </label>
          )}

          {fieldError?.field === 'form' && (
            <div className="notice danger compact">
              <p>{fieldError.message}</p>
              {inProgressId && (
                <button
                  type="button"
                  className="small"
                  onClick={() => {
                    onClose();
                    navigate(`/billing/return/${inProgressId}`);
                  }}
                >
                  Check that payment
                </button>
              )}
            </div>
          )}

          <button type="submit" className="primary block large" disabled={busy || (method === 'card' && card.state !== 'ready')}>
            {busy ? <Spinner /> : null}
            {method === 'hosted' ? `Continue to PayU — ${amount}` : `Pay ${amount}`}
          </button>

          <p className="fine consent">
            Payment is processed by PayU S.A. By paying you accept the{' '}
            <a href={TERMS_URL} target="_blank" rel="noreferrer">
              PayU payment terms
            </a>
            . PayU S.A. is the controller of your payment data —{' '}
            <a href={PRIVACY_URL} target="_blank" rel="noreferrer">
              privacy information
            </a>
            .
          </p>
          <div className="wall-foot">
            <button type="button" className="ghost" onClick={close} disabled={busy}>
              Cancel
            </button>
          </div>
      </form>

      {step === 'redirect' && (
        <Outcome icon={<Spinner />} title={method === 'card' ? 'Confirming with your bank…' : 'Opening PayU…'}>
          {method === 'card'
            ? 'Your bank checks that it is really you (3-D Secure). You come back here right after.'
            : 'Finish the payment on PayU’s page; you come back here afterwards.'}
        </Outcome>
      )}

      {step === 'wait' && (
        <Outcome icon={<span className="phone" aria-hidden="true" />} title="Confirm in your banking app">
          <p>Open your bank’s app and approve the payment of {amount}.</p>
          <div className="countdown" aria-live="polite">
            <span>
              {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}
            </span>
            <div className="meter">
              <span style={{ width: `${(secondsLeft / BLIK_SECONDS) * 100}%` }} />
            </div>
          </div>
          <p className="fine">Keep this window open — it updates by itself.</p>
        </Outcome>
      )}

      {step === 'done' && (
        <Outcome icon={<span className="result-icon success">✓</span>} title="Payment received">
          <p>
            {shortName} is active{payment?.periodEnd ? ` until ${formatDate(payment.periodEnd)}` : ''}. Your devices pick it up the next time
            they sync.
          </p>
          <button type="button" className="primary" onClick={close}>
            Done
          </button>
        </Outcome>
      )}

      {step === 'failed' && (
        <Outcome icon={<span className="result-icon failed">!</span>} title={payment?.status === 'pending' ? 'Not confirmed yet' : 'Payment not completed'}>
          <p>
            {payment?.status === 'pending'
              ? 'Your bank has not confirmed it yet. If you approved it, your plan switches on as soon as PayU confirms — check Payment history in a moment.'
              : 'The bank did not approve it, or the code expired. Nothing was charged.'}
          </p>
          <div className="button-row center-row">
            {payment?.status !== 'pending' && (
              <button
                type="button"
                className="primary"
                onClick={() => {
                  setStep('pay');
                  setBlikCode('');
                  setPayment(null);
                }}
              >
                Try again
              </button>
            )}
            <button type="button" onClick={close}>
              Close
            </button>
          </div>
        </Outcome>
      )}
    </dialog>
  );
}

function Outcome({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <div className="wall-outcome">
      <div className="outcome-icon">{icon}</div>
      <h3>{title}</h3>
      {children}
    </div>
  );
}

function MethodIcon({ method }: { method: PaymentMethod }) {
  if (method === 'blik') {
    return (
      <svg width="30" height="20" viewBox="0 0 30 20" aria-hidden="true">
        <rect width="30" height="20" rx="4" fill="#000" />
        <text x="15" y="14" fill="#fff" fontSize="9" fontWeight="700" textAnchor="middle" fontFamily="system-ui, sans-serif">
          blik
        </text>
      </svg>
    );
  }
  const path =
    method === 'card'
      ? 'M3 6.5A1.5 1.5 0 0 1 4.5 5h15A1.5 1.5 0 0 1 21 6.5v11a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 17.5zM3 10h18M7 15h4'
      : 'M4 10h16M4 10l8-6 8 6M6 10v8M10 10v8M14 10v8M18 10v8M4 20h16';
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={path} />
    </svg>
  );
}

/* --- PayU Secure Form ----------------------------------------------------- */

interface PayUField {
  render(selector: string): PayUField;
  clear(): void;
  on(event: string, handler: (body: unknown) => void): void;
}
interface PayUSdk {
  secureForms(options?: Record<string, unknown>): { add(type: 'number' | 'date' | 'cvv', options?: Record<string, unknown>): PayUField };
  tokenize(type: 'SINGLE'): Promise<{ status: string; body?: { token: string; mask: string }; error?: { messages?: Array<{ message: string }> } }>;
}
declare global {
  interface Window {
    PayU?: (posId: string) => PayUSdk;
  }
}

const scripts = new Map<string, Promise<void>>();
function loadScript(src: string): Promise<void> {
  if (!scripts.has(src)) {
    scripts.set(
      src,
      new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => {
          scripts.delete(src);
          reject(new Error(`could not load ${src}`));
        };
        document.head.append(script);
      }),
    );
  }
  return scripts.get(src)!;
}

/**
 * Mounts PayU's card fields once, the first time the card tab is shown, and
 * keeps them for the life of the page; later opens only clear them.
 */
function useSecureForm(billing: BillingOverview, active: boolean) {
  const [state, setState] = useState<'idle' | 'loading' | 'ready' | 'failed'>('idle');
  const sdkRef = useRef<PayUSdk | null>(null);
  const fieldsRef = useRef<PayUField[]>([]);

  useEffect(() => {
    if (!active) return;
    if (sdkRef.current) {
      fieldsRef.current.forEach((field) => field.clear());
      return;
    }
    const payu = billing.checkout.payu;
    if (!payu?.posId) {
      setState('failed');
      return;
    }
    let cancelled = false;
    setState('loading');
    loadScript(payu.sdkUrl)
      .then(() => {
        if (cancelled || !window.PayU) return;
        const dark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
        const sdk = window.PayU(payu.posId!);
        const forms = sdk.secureForms({ lang: 'en' });
        const style = {
          basic: { fontColor: dark ? '#eceff4' : '#121620', fontSize: '16px', fontWeight: '500' },
          invalid: { fontColor: dark ? '#ff7566' : '#c2372a' },
          placeholder: { fontColor: dark ? '#6b7385' : '#9aa1ae' },
        };
        fieldsRef.current = [
          forms.add('number', { style, cardIcon: true, placeholder: { number: '0000 0000 0000 0000' } }).render('#payu-card-number'),
          forms.add('date', { style, placeholder: { date: 'MM / YY' } }).render('#payu-card-date'),
          forms.add('cvv', { style, placeholder: { cvv: 'CVC' } }).render('#payu-card-cvv'),
        ];
        sdkRef.current = sdk;
        setState('ready');
      })
      .catch(() => !cancelled && setState('failed'));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  return {
    state,
    async tokenize(): Promise<{ token: string } | { error: string }> {
      if (!sdkRef.current) return { error: 'The card form is not ready yet.' };
      try {
        const result = await sdkRef.current.tokenize('SINGLE');
        if (result.status === 'SUCCESS' && result.body?.token) return { token: result.body.token };
        const messages = (result.error?.messages ?? []).map((message) => message.message).filter(Boolean);
        return { error: messages.length ? messages.join(' ') : 'Check the card details.' };
      } catch {
        return { error: 'The card details could not be read. Please enter them again.' };
      }
    },
  };
}
